/**
 * Facciata di PostHog (T206-FE, passo a).
 *
 * `posthog-js` pesa decine di KB: se un modulo dell'ingresso lo importa in modo statico finisce
 * nel chunk d'ingresso e ritarda il primo render. Nessun file dell'app importa più `posthog-js`
 * (se non come `import type`) né `@posthog/react` (che lo importa a sua volta in modo statico):
 * tutti passano da qui. La libreria viene scaricata con un import dinamico da `loadPostHog()`,
 * avviato da `main.tsx` dopo il primo render.
 *
 * Finché la libreria non è pronta le chiamate (capture, register, identify, reset, set_config,
 * setPersonProperties) vanno in una coda e vengono consegnate nello stesso ordine subito dopo
 * `init()` e `register()` di avvio: l'ordine «init → preferenza di consenso» resta quindi quello
 * storico anche quando il consenso viene applicato prima che il modulo arrivi. Gli eventi in
 * coda portano il timestamp del momento in cui sono stati richiesti, non quello della consegna.
 */
import type { CaptureOptions, PostHog, PostHogConfig, Properties } from 'posthog-js';

type Status = 'idle' | 'loading' | 'ready' | 'failed' | 'disabled';
type Call = (client: PostHog) => void;
interface QueuedCall {
  call: Call;
  /** Consenso e identità (reset, set_config, identify, register): non si scartano mai. */
  essential: boolean;
}

/** Oltre questa soglia si scartano le capture più vecchie: protegge da un caricamento mai avviato. */
const MAX_QUEUED_CALLS = 500;
/** Attesa massima dell'evento `load` prima di scaricare PostHog comunque. */
const MAX_WAIT_FOR_LOAD_MS = 4000;
const IDLE_TIMEOUT_MS = 1000;

let client: PostHog | null = null;
let status: Status = 'idle';
let queue: QueuedCall[] = [];
let loading: Promise<void> | null = null;

function dispatch(call: Call, essential: boolean): void {
  if (status === 'ready' && client) {
    call(client);
    return;
  }
  if (status === 'disabled' || status === 'failed') return;
  if (queue.length >= MAX_QUEUED_CALLS) {
    // A coda piena si scarta la capture più vecchia; le chiamate di consenso e identità restano.
    const oldest = queue.findIndex((queued) => !queued.essential);
    if (oldest >= 0) queue.splice(oldest, 1);
    else if (!essential) return;
  }
  queue.push({ call, essential });
}

export interface PostHogFacade {
  capture(event: string, properties?: Properties | null, options?: CaptureOptions): void;
  register(properties: Properties): void;
  identify(id?: string, set?: Properties, setOnce?: Properties): void;
  reset(): void;
  set_config(config: Partial<PostHogConfig>): void;
  setPersonProperties(set?: Properties, setOnce?: Properties): void;
}

export const posthog: PostHogFacade = {
  capture(event, properties, options) {
    // Il timestamp dell'evento è quello della richiesta, anche se la consegna avviene dopo.
    const requestedAt = new Date();
    dispatch(
      (c) =>
        c.capture(
          event,
          properties,
          status === 'ready'
            ? options
            : { ...options, timestamp: options?.timestamp ?? requestedAt }
        ),
      false
    );
  },
  register(properties) {
    dispatch((c) => c.register(properties), true);
  },
  identify(id, set, setOnce) {
    dispatch((c) => c.identify(id, set, setOnce), true);
  },
  reset() {
    dispatch((c) => c.reset(), true);
  },
  set_config(config) {
    dispatch((c) => c.set_config(config), true);
  },
  setPersonProperties(set, setOnce) {
    dispatch((c) => c.setPersonProperties(set, setOnce), false);
  },
};

export interface LoadPostHogOptions {
  apiKey: string;
  config: Partial<PostHogConfig>;
  /** Proprietà registrate su ogni evento subito dopo `init()` (es. `app_build`, `display_mode`). */
  superProperties: Properties;
}

/**
 * Scarica `posthog-js`, lo inizializza e consegna la coda. Idempotente: le chiamate successive
 * restituiscono la stessa promessa. Non rifiuta mai: se lo scaricamento fallisce le chiamate
 * successive vengono scartate (l'analitica non deve mai rompere l'app).
 */
export function loadPostHog(options: LoadPostHogOptions): Promise<void> {
  if (status === 'disabled') return Promise.resolve();
  if (loading) return loading;
  status = 'loading';
  loading = import('posthog-js')
    .then(({ default: loaded }) => {
      loaded.init(options.apiKey, options.config);
      // L'istanza è inizializzata: da qui la coda va consegnata comunque, anche se register lancia.
      client = loaded;
      try {
        loaded.register(options.superProperties);
      } catch {
        // le super-proprietà mancanti non devono far perdere consenso, identità ed eventi
      }
      // Lo stato resta `loading` durante la consegna: una chiamata fatta da un callback della
      // libreria mentre si svuota la coda si accoda in fondo e non scavalca le precedenti.
      for (let next = queue.shift(); next; next = queue.shift()) {
        try {
          next.call(loaded);
        } catch {
          // una chiamata rotta non deve far perdere le successive
        }
      }
      status = 'ready';
    })
    .catch(() => {
      status = 'failed';
      queue = [];
    });
  return loading;
}

/**
 * Cancella subito, in modo sincrono, i dati durevoli scritti da posthog-js: la chiave
 * `ph_<token>_posthog` in localStorage e il cookie con lo stesso nome. Serve alla revoca del consenso
 * quando la libreria non c'è (ancora) e il `reset()` è solo in coda. Nome da posthog-persistence.js
 * (`'ph_' + token + '_posthog'`); il dominio del cookie lo sceglie storage.js: host corrente oppure
 * il primo suffisso non pubblico (`.dominio`), quindi si scade il cookie su host e su ogni suffisso.
 */
export function purgeDurablePostHogStorage(): void {
  const isPostHogKey = (name: string) => /^ph_.+_posthog$/.test(name);
  try {
    for (const key of Object.keys(localStorage)) {
      if (isPostHogKey(key)) localStorage.removeItem(key);
    }
  } catch {
    // localStorage può non essere disponibile
  }
  try {
    const labels = window.location.hostname.split('.');
    const domains = [''];
    for (let i = 0; i < labels.length - 1 && i < 8; i += 1) {
      domains.push(`; domain=.${labels.slice(i).join('.')}`);
    }
    for (const entry of document.cookie.split(';')) {
      const name = entry.split('=')[0]?.trim() ?? '';
      if (!isPostHogKey(name)) continue;
      for (const domain of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
      }
    }
  } catch {
    // cookie non disponibili
  }
}

/** Host non tracciato (loopback, tunnel): PostHog non viene mai scaricato e le chiamate si scartano. */
export function disablePostHog(): void {
  status = 'disabled';
  queue = [];
}

/**
 * Esegue `task` dopo il primo render: alla fine del caricamento della pagina (entro un tetto
 * di attesa) e poi quando il browser è inattivo, così lo scaricamento non compete con la
 * foto e il JS da cui dipende l'LCP.
 */
export function runAfterFirstRender(task: () => void): void {
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(task, { timeout: IDLE_TIMEOUT_MS });
    } else {
      window.setTimeout(task, 0);
    }
  };
  if (document.readyState === 'complete') {
    start();
    return;
  }
  window.addEventListener('load', start, { once: true });
  window.setTimeout(start, MAX_WAIT_FOR_LOAD_MS);
}

/** Solo per i test: riporta la facciata allo stato iniziale. */
export function resetPostHogClientForTests(): void {
  client = null;
  status = 'idle';
  queue = [];
  loading = null;
}
